import React, { useState } from "react";
import ImageRenderer from "./ImageRenderer";
import MarkdownRenderer from "./MarkdownRenderer";
import MapRenderer from "./MapRenderer";
import RevealActionButtons from "./RevealActionButtons";
import InteractiveStreetView from "./InteractiveStreetView";
import PredictionRevealRenderer, { PredictionTarget } from "./PredictionRevealRenderer";
import PredictionResultCard from "./PredictionResultCard";
import { processContentWithImages } from "../../utils/imageUtils";
import { cleanAIResponse, processContentForLinks } from "../../utils/textCleaningUtils";
import { extractMapPreviews } from "../../utils/mapUtils";
import { DiagnosticUtils } from "../../utils/diagnosticUtils";
import { targetService } from "@/services/targetService";
import { useChatOptional } from "@/context/ChatContext";

interface RegularContentRendererProps {
  content: string;
  type: "system" | "user";
  unsplashData?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

const RegularContentRenderer: React.FC<RegularContentRendererProps> = ({ content, type, unsplashData }) => {
  const [currentViewState, setCurrentViewState] = useState<{
    lat: number;
    lng: number;
    heading: number;
    pitch: number;
  } | undefined>(undefined);
  const [userPrediction, setUserPrediction] = useState<'YES' | 'NO' | undefined>();
  const chat = useChatOptional();
  const currentSessionId = chat?.currentSessionId;
  const addPredictionResultMessage = chat?.addPredictionResultMessage;

  // Check if this is a prediction RESULT (after selection confirmed)
  const predictionResultMatch = content.match(/PREDICTION_RESULT:(.+)/s);
  if (predictionResultMatch) {
    try {
      const resultData = JSON.parse(predictionResultMatch[1]) as {
        predictionTarget: PredictionTarget & { resultStatus?: 'correct' | 'incorrect' };
        userPrediction: 'YES' | 'NO';
      };
      return (
        <PredictionResultCard 
          predictionTarget={resultData.predictionTarget}
          userPrediction={resultData.userPrediction}
          resultStatus={resultData.predictionTarget.resultStatus}
        />
      );
    } catch (e) {
      console.error('Failed to parse prediction result data:', e);
    }
  }

  // Check if this is a prediction target reveal (selection phase)
  const predictionMatch = content.match(/PREDICTION_TARGET:(.+)/s);
  if (predictionMatch) {
    try {
      const predictionData = JSON.parse(predictionMatch[1]) as PredictionTarget & {
        storedUserPrediction?: 'YES' | 'NO';
        resultStatus?: 'correct' | 'incorrect';
      };

      const storedUserPrediction = predictionData.storedUserPrediction;
      const effectiveUserPrediction = userPrediction ?? storedUserPrediction;
      const isReadOnly = !chat;
      
      // If the event is resolved OR we have a stored correctness result AND we have a user prediction,
      // show the result card with correct/incorrect styling.
      const isResolved = !!predictionData.event?.resolved_outcome || !!predictionData.resultStatus;
      if (isResolved && effectiveUserPrediction) {
        return (
          <PredictionResultCard 
            predictionTarget={predictionData}
            userPrediction={effectiveUserPrediction}
            resultStatus={predictionData.resultStatus}
          />
        );
      }

      const handlePredictionSubmit = async (prediction: 'YES' | 'NO') => {
        if (import.meta.env.DEV) {
          console.log('🎯 PREDICTION SUBMIT: Starting submission flow', {
            prediction,
            currentSessionId,
            targetId: predictionData.id,
            isReadOnly,
            hasAddPredictionResultMessage: !!addPredictionResultMessage
          });
        }
        
        setUserPrediction(prediction);

        // In historic/read-only contexts there is no ChatProvider; don't write anything.
        if (!currentSessionId || !predictionData.id || isReadOnly) {
          if (import.meta.env.DEV) console.log('⚠️ PREDICTION SUBMIT: Skipping database write (read-only or missing IDs)');
          return;
        }

        const stored = await targetService.storeUserPrediction(currentSessionId, predictionData.id, prediction);
        if (import.meta.env.DEV) console.log('🎯 PREDICTION SUBMIT: storeUserPrediction result:', stored);

        // Add a new message with the result card
        if (addPredictionResultMessage) {
          if (import.meta.env.DEV) console.log('🎯 PREDICTION SUBMIT: Calling addPredictionResultMessage');
          addPredictionResultMessage(predictionData, prediction);
        } else if (import.meta.env.DEV) {
          console.log('⚠️ PREDICTION SUBMIT: addPredictionResultMessage not available');
        }
      };

      return (
        <PredictionRevealRenderer
          predictionTarget={predictionData}
          onPredictionSubmit={handlePredictionSubmit}
          userPrediction={effectiveUserPrediction}
          // Force revealed in read-only views so the confirm UI never appears.
          isRevealed={isReadOnly || !!effectiveUserPrediction}
        />
      );
    } catch (e) {
      console.error('Failed to parse prediction target data:', e);
    }
  }

  if (import.meta.env.DEV) console.log('📝 CHATMESSAGE-RENDER: Non-feedback content - starting processing pipeline');

  // Only process non-feedback content through the complex pipeline for backward compatibility
  const cleanedContent = type === "system" ? cleanAIResponse(content) : content;
  if (import.meta.env.DEV) DiagnosticUtils.analyzeContentProcessing(cleanedContent, 'After cleanAIResponse');

  // Process content with images and links
  const processedContent = processContentWithImages(cleanedContent, processContentForLinks);
  const { text, images, imageUrls, unsplashData: extractedUnsplashData } = processedContent;
  
  // Use passed unsplashData or extracted unsplashData
  const finalUnsplashData = unsplashData || extractedUnsplashData;
  
  if (import.meta.env.DEV) DiagnosticUtils.analyzeContentProcessing(text || '', 'After processContentWithImages');
  
  // Determine context for map preview extraction
  let mapContext: 'generation' | 'reveal' | 'feedback' | undefined;
  const isRevealMessage = text?.includes('Here\'s your') && text?.includes('target');
  if (isRevealMessage) {
    mapContext = 'reveal';
  } else if (text?.includes('generated') || text?.includes('ready for your remote viewing')) {
    mapContext = 'generation';
  }
  
  const { text: finalText, mapUrls } = extractMapPreviews(text, mapContext);
  if (import.meta.env.DEV) DiagnosticUtils.analyzeContentProcessing(finalText || '', 'Final Processed Text');

  // Helper function to extract coordinates from URL for RevealActionButtons
  const extractCoordinatesFromUrl = (url: string) => {
    const atMatch = url.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
    if (atMatch) {
      return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
    }
    return undefined;
  };

  if (import.meta.env.DEV) {
    console.log('🗺️ CHATMESSAGE-RENDER: Map context determined:', mapContext);
    console.log('🗺️ CHATMESSAGE-RENDER: Final mapUrls to render:', mapUrls);
    console.log('🖼️ CHATMESSAGE-RENDER: Image URLs to render:', imageUrls);
    console.log('🖼️ CHATMESSAGE-RENDER: Base64 images to render:', images.length);
    console.log('🎯 CHATMESSAGE-RENDER: Is reveal message:', isRevealMessage);
  }

  // For reveal messages, render text first, then images
  if (isRevealMessage) {
    return (
      <>
        {finalText && (
          <MarkdownRenderer content={finalText} type={type} />
        )}
        <ImageRenderer 
          images={images} 
          imageUrls={imageUrls}
          showActionButtons={isRevealMessage}
          unsplashData={finalUnsplashData}
        />
        <MapRenderer 
          mapUrls={mapUrls} 
          onViewChange={setCurrentViewState}
        />
        {isRevealMessage && mapUrls.length > 0 && (
          <RevealActionButtons
            targetUrl={mapUrls[0]}
            currentViewState={currentViewState}
            coordinates={currentViewState ? { lat: currentViewState.lat, lng: currentViewState.lng } : extractCoordinatesFromUrl(mapUrls[0])}
          />
        )}
      </>
    );
  }

  // For all other messages, render images first (existing behavior)
  return (
    <>
      <ImageRenderer 
        images={images} 
        imageUrls={imageUrls}
        unsplashData={finalUnsplashData}
      />
      {finalText && (
        <MarkdownRenderer content={finalText} type={type} />
      )}
      <MapRenderer 
        mapUrls={mapUrls} 
        onViewChange={setCurrentViewState}
      />
    </>
  );
};

export default RegularContentRenderer;